import { Component, computed, forwardRef, inject, signal, viewChild } from '@angular/core';
import { AbstractFormControl } from '../abstract-form-control';
import { CorporateGroupItemDto, CorporateGroupService } from '@artemis-software/wr-api';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { asyncComputed } from '@/utils/signals/asyncComputed';
import { NbPopoverDirective } from '@nebular/theme';
import { watch } from '@/utils/watch';

@Component({
  standalone: false,
  selector: 'wr-corporate-group-select',
  templateUrl: './corporate-group-select.component.html',
  styleUrls: ['./corporate-group-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CorporateGroupSelectComponent),
      multi: true,
    },
  ],
})
export class CorporateGroupSelectComponent extends AbstractFormControl<string | undefined> {

  readonly popover = viewChild(NbPopoverDirective);

  readonly corporateGroupService = inject(CorporateGroupService);

  readonly filterText = signal('');

  readonly allGroups = asyncComputed(() => this.corporateGroupService.findAll({}));

  readonly filteredGroups = computed(() => {
    const all = this.allGroups() ?? [];
    const text = this.filterText().toLowerCase().trim();
    if (!text) return all;
    return all.filter(group => group.name.toLowerCase().includes(text));
  });

  constructor() {
    super();
    watch([this.allGroups, this.valueSignal], () => {
      const found = (this.allGroups() ?? []).find(group => group.id === this.value);
      if (found) {
        this.filterText.set(found.name);
      }
    });
  }

  override writeValue(id: string | undefined): void {
    super.writeValue(id);
    if (!id) {
      this.filterText.set('');
      return;
    }
    const found = (this.allGroups() ?? []).find(group => group.id === id);
    if (found) {
      this.filterText.set(found.name);
    }
  }

  onSelectionChange(group: CorporateGroupItemDto): void {
    this.filterText.set(group.name);
    this.value = group.id;
    this.popover()?.hide();
  }

  onClear(): void {
    this.value = undefined;
    this.filterText.set('');
  }
}
